import { ArrowUp } from 'lucide-react';
import { useScrollProgress } from '../hooks/use-scroll-progress';
import { useScrolled } from '../hooks/use-scrolled';

/**
 * Voltar ao topo — com o progresso de leitura no anel.
 *
 * ## Por que existe
 * A marca no cabeçalho já leva ao topo (`href="#inicio"`), e o dono apontou o
 * problema: ninguém sabe disso. Conhecimento oculto não é atalho, é função
 * que não existe. Numa página de ~11.000px, quem chega ao rodapé não tem
 * caminho de volta visível.
 *
 * ## Por que é `<a>`, e não `<button>`
 * Um link para `#inicio` navega de verdade: leva a ROLAGEM e o FOCO, funciona
 * sem JavaScript, e o `scroll-behavior: smooth` do `estilos.css` já cuida da
 * suavidade — inclusive desligando-a sozinho sob `prefers-reduced-motion`,
 * pela regra global. Um `<button>` com `scrollTo` teria de reimplementar as
 * três coisas, e erraria o foco (o leitor de tela continuaria no rodapé).
 *
 * ## O anel não é enfeite
 * É o mesmo `useScrollProgress` da barra do topo, agora onde o olho está: a
 * pessoa vê quanto falta antes de decidir se rola ou volta. `pathLength={100}`
 * normaliza o círculo, então o `strokeDashoffset` é a porcentagem direta —
 * sem depender do raio.
 *
 * Só aparece depois de 900px, para não disputar a primeira tela com o herói.
 */
export function BotaoTopo(): JSX.Element {
  const progresso = useScrollProgress();
  const visivel = useScrolled(900);

  return (
    <a
      href="#inicio"
      aria-label="Voltar ao topo"
      /* `aria-hidden` e `tabIndex` fora de alcance enquanto escondido: um
         link invisível no caminho do Tab é armadilha de teclado. */
      aria-hidden={!visivel}
      tabIndex={visivel ? undefined : -1}
      className={`border-auth-borda/12 bg-auth-fundo-2/90 text-auth-texto-secundario hover:text-auth-texto hover:border-auth-marca-hover/40 fixed right-5 bottom-5 z-40 grid size-11 place-items-center rounded-full border shadow-[0_10px_30px_-10px_rgb(0_0_0/0.9)] transition-[opacity,transform,color,border-color] duration-300 ${
        visivel
          ? 'pointer-events-auto translate-y-0 opacity-100'
          : 'pointer-events-none translate-y-3 opacity-0'
      }`}
    >
      <svg aria-hidden className="absolute inset-0 -rotate-90" viewBox="0 0 44 44">
        <circle
          cx="22"
          cy="22"
          r="20"
          fill="none"
          strokeWidth="2"
          className="stroke-auth-borda/12"
        />
        <circle
          cx="22"
          cy="22"
          r="20"
          fill="none"
          strokeWidth="2"
          strokeLinecap="round"
          pathLength={100}
          strokeDasharray="100"
          strokeDashoffset={100 - Math.round(progresso * 100)}
          className="stroke-auth-marca-hover"
        />
      </svg>
      <ArrowUp size={17} aria-hidden className="relative" />
    </a>
  );
}
